import {
  StartOfWeekOptions,
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  getMonth,
  isLastDayOfMonth,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns'

import { FixedDuration } from './constants'

export type Period = 'day' | 'week' | 'month' | 'year'

export type Duration = {
  period: Period
  duration: number
}

const WEEK_STARTS_ON = 1

/**
 * Converts a start and end date to a known period duration.
 *
 * If there are multiple possible durations, the bigger one is returned.
 */
export const detectDuration = (startDate: Date, endDate: Date): Duration => {
  const exclusiveEndDate = addDays(endDate, 1)

  const startMonth = getMonth(startDate)
  const endMonth = getMonth(exclusiveEndDate)

  if (isSameMonthDay(startDate, exclusiveEndDate)) {
    if (startMonth === endMonth) {
      // Compare months only, ignoring the year
      return { period: 'year', duration: exclusiveEndDate.getFullYear() - startDate.getFullYear() }
    }

    return { period: 'month', duration: differenceInCalendarMonths(exclusiveEndDate, startDate) }
  }

  const daysDifference = differenceInCalendarDays(exclusiveEndDate, startDate)

  if (daysDifference % 7 === 0) {
    return { period: 'week', duration: daysDifference / 7 }
  }

  return {
    period: 'day',
    duration: daysDifference,
  }
}

export const moveRange = (
  startDate: Date,
  endDate: Date,
  direction: 'forward' | 'backward'
): [Date, Date] => {
  const { period, duration: positiveDuration } = detectDuration(startDate, endDate)

  const duration = direction === 'forward' ? positiveDuration : -positiveDuration

  switch (period) {
    case 'day':
      return [addDays(startDate, duration), addDays(endDate, duration)]
    case 'week':
      return [addWeeks(startDate, duration), addWeeks(endDate, duration)]
    case 'month': {
      return [
        addMonthsStickingToEnd(startDate, duration),
        addMonthsStickingToEnd(endDate, duration),
      ]
    }
    case 'year':
      return [
        addMonthsStickingToEnd(startDate, duration * 12),
        addMonthsStickingToEnd(endDate, duration * 12),
      ]
  }
}

export const calculateRegularRange = (regularPeriods: FixedDuration, date: Date): [Date, Date] => {
  const option: StartOfWeekOptions = { weekStartsOn: WEEK_STARTS_ON } // Week should start on Monday

  switch (regularPeriods) {
    case 'day':
      return [date, date]

    case 'week':
      return [startOfWeek(date, option), endOfWeek(date, option)]

    case 'fortnight':
      return [startOfWeek(date, option), endOfWeek(addWeeks(date, 1), option)]

    case 'month':
      return [startOfMonth(date), endOfMonth(date)]

    case 'quarter':
      return [startOfQuarter(date), endOfQuarter(date)]

    case 'year':
      return [startOfYear(date), endOfYear(date)]

    case 'year_to_date':
      return [startOfYear(date), date]
  }
}

/**
 * Adds a number of months to the date, keeping the end of the month if it was the end of the month.
 *
 * For example, when adding 1 month, converts February 28th to March 31st, instead of to March 28th
 */
const addMonthsStickingToEnd = (date: Date, months: number) => {
  const newDate = addMonths(date, months)

  if (isLastDayOfMonth(date)) {
    return endOfMonth(newDate)
  }

  return newDate
}

const isSameMonthDay = (dateA: Date, dateB: Date) => {
  const isDateALastDayOfMonth = isLastDayOfMonth(dateA)
  const isDateBLastDayOfMonth = isLastDayOfMonth(dateB)

  if (isDateALastDayOfMonth && isDateBLastDayOfMonth) {
    return true
  }

  // Even if one of the dates is the last day of the month but not the other,
  // they aren't the same day
  if (isDateALastDayOfMonth || isDateBLastDayOfMonth) {
    return false
  }

  return dateA.getDate() === dateB.getDate()
}
